import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

import AnswerBlocks from './AnswerBlocks';
import { CANCEL_AFTER_SECONDS, errorAdvice, providerLabel, waitingHint } from './assistantHints';
import ConversationList from './ConversationList';
import { restoreMessages } from './conversationUtils';
import {
  askAssistant, assistantErrorKind, assistantErrorMessage, getAssistantConversation, hideAssistantConversation, listAssistantConversations,
} from '../../services/AssistantService';
import SnackbarUtils from '../../utils/SnackbarUtils';

const SUGGESTIONS = [
  '¿De qué insumos me queda poco stock?',
  '¿Cómo me fue con las ventas ayer?',
  '¿Cuáles son los 5 productos más vendidos este mes?',
];

const HISTORY_LIMIT = 10;

/** Un error o una cancelación: qué pasó, qué hacer y los botones para hacerlo. */
const Problem = ({ message, canManage, onRetry, onOpenSettings, disabled }) => {
  const canceled = message.kind === 'CANCELED';
  const advice = errorAdvice(message.kind, canManage);
  return (
    <Alert severity={canceled ? 'info' : 'error'} variant="outlined" sx={{ alignItems: 'flex-start' }}>
      <Typography variant="body2" fontWeight={600}>{canceled ? 'Cancelaste la consulta.' : message.text}</Typography>
      {advice.text && <Typography variant="body2" sx={{ mt: 0.5 }}>{advice.text}</Typography>}
      {(advice.retry || advice.settings) && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          {advice.retry && message.question && (
            <Button size="small" variant="outlined" onClick={onRetry} disabled={disabled}>Reintentar</Button>
          )}
          {advice.settings && <Button size="small" onClick={onOpenSettings}>Ir a Configuración</Button>}
        </Stack>
      )}
    </Alert>
  );
};

const Message = ({ message, canManage, onRetry, onOpenSettings, busy }) => {
  if (message.role === 'user') {
    return (
      <Box sx={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
        <Paper sx={{ px: 2, py: 1, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{message.text}</Typography>
        </Paper>
      </Box>
    );
  }
  if (message.isError) {
    return (
      <Box sx={{ alignSelf: 'flex-start', maxWidth: '92%' }}>
        <Problem message={message} canManage={canManage} onRetry={() => onRetry(message)} onOpenSettings={onOpenSettings} disabled={busy} />
      </Box>
    );
  }
  return (
    <Box sx={{ alignSelf: 'flex-start', maxWidth: '92%' }}>
      <Paper variant="outlined" sx={{ px: 2, py: 1.25, borderRadius: 2 }}>
        <AnswerBlocks text={message.text} />
        {!message.completed && (
          <Chip size="small" color="warning" variant="outlined" label="Respuesta incompleta" sx={{ mt: 1 }} />
        )}
      </Paper>
    </Box>
  );
};

export default function AssistantChat({ status, canManage, onOpenSettings }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const endRef = useRef(null);
  const abortRef = useRef(null);

  const loadConversations = async () => {
    try {
      setConversations(await listAssistantConversations());
    } catch (error) {
      // La lista es una comodidad: si falla, el chat sigue funcionando.
    }
  };

  useEffect(() => { loadConversations(); }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [messages, loading, elapsed]);

  // Cuenta los segundos de espera para poder explicar la demora y ofrecer cancelar.
  useEffect(() => {
    if (!loading) return undefined;
    setElapsed(0);
    const timer = setInterval(() => setElapsed((seconds) => seconds + 1), 1000);
    return () => clearInterval(timer);
  }, [loading]);

  const send = async (text, base = messages) => {
    const question = (text ?? input).trim();
    if (!question || loading) return;

    const history = base
      .filter((message) => !message.isError && message.text)
      .slice(-HISTORY_LIMIT)
      .map((message) => ({ role: message.role, text: message.text }));

    const controller = new AbortController();
    abortRef.current = controller;
    setMessages([...base, { role: 'user', text: question }]);
    setInput('');
    setLoading(true);
    try {
      const result = await askAssistant({ question, history, conversationId, signal: controller.signal });
      setConversationId(result.conversationId || conversationId);
      setMessages((current) => [...current, {
        role: 'assistant', text: result.answer, completed: result.completed,
      }]);
    } catch (error) {
      setMessages((current) => [...current, {
        role: 'assistant', isError: true, question, kind: assistantErrorKind(error), text: assistantErrorMessage(error),
      }]);
    } finally {
      abortRef.current = null;
      setLoading(false);
      loadConversations();
    }
  };

  const cancel = () => abortRef.current?.abort();

  // Reintentar saca el intento fallido (la pregunta y su error) y vuelve a preguntar lo mismo.
  const retry = (failed) => {
    const index = messages.lastIndexOf(failed);
    const base = index > 0 && messages[index - 1].role === 'user' ? messages.slice(0, index - 1) : messages.slice(0, index);
    send(failed.question, base);
  };

  const reset = () => {
    setMessages([]);
    setConversationId(null);
    setInput('');
  };

  // Retomar un chat guardado: se cargan sus mensajes y las preguntas nuevas siguen en la misma conversación.
  const openConversation = async (id) => {
    if (loading || id === conversationId) return;
    try {
      const saved = await getAssistantConversation(id);
      setMessages(restoreMessages(saved));
      setConversationId(id);
      setInput('');
    } catch (error) {
      SnackbarUtils.error(assistantErrorMessage(error));
      loadConversations();
    }
  };

  const hideConversation = async (conversation) => {
    if (!window.confirm(`¿Quitar "${conversation.title.slice(0, 60)}" de tu lista? El historial de la empresa se conserva.`)) return;
    try {
      await hideAssistantConversation(conversation.conversationId);
      if (conversation.conversationId === conversationId) reset();
      await loadConversations();
    } catch (error) {
      SnackbarUtils.error(assistantErrorMessage(error));
    }
  };

  if (!status.configured) {
    return (
      <Alert
        severity="info"
        action={canManage && <Button color="inherit" size="small" onClick={onOpenSettings}>Configurar</Button>}
      >
        {canManage
          ? 'El asistente todavía no tiene una clave de API. Cargala en Configuración para empezar a preguntar.'
          : 'El asistente todavía no está configurado. Pedile a quien administra la empresa que cargue la clave de API.'}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, height: { md: 'calc(100vh - 330px)' }, minHeight: { md: 420 } }}>
      <ConversationList
        conversations={conversations} selectedId={conversationId} busy={loading}
        onSelect={openConversation} onNew={reset} onHide={hideConversation}
      />
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, height: { xs: 'calc(100vh - 330px)', md: 'auto' }, minHeight: 420, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Chip size="small" variant="outlined" label={`${providerLabel(status.provider)}${status.model ? ` · ${status.model}` : ''}`} />
          <Typography variant="caption" color="text.secondary">
            Consulta tus datos en modo solo lectura. Cada pregunta queda guardada en el historial de la empresa.
          </Typography>
        </Stack>
      </Stack>

      <Stack spacing={1.5} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {messages.length === 0 && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: 2 }}>
            <Box sx={{ width: '100%', maxWidth: 640 }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>¿Qué querés saber de tu negocio?</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Preguntá en tus palabras: ventas, stock, costos, compras o clientes.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
                {SUGGESTIONS.map((suggestion) => (
                  <Chip
                    key={suggestion} label={suggestion} onClick={() => send(suggestion)} variant="outlined" clickable
                    sx={{ height: 'auto', py: 0.75, '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'center' } }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
        {messages.map((message, index) => (
          <Message key={index} message={message} canManage={canManage} onRetry={retry} onOpenSettings={onOpenSettings} busy={loading} />
        ))}
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: 'flex-start', color: 'text.secondary' }}>
            <CircularProgress size={16} />
            <Typography variant="body2">{waitingHint(elapsed, status.provider)}</Typography>
            {elapsed >= CANCEL_AFTER_SECONDS && <Button size="small" onClick={cancel}>Cancelar</Button>}
          </Stack>
        )}
        <div ref={endRef} />
      </Stack>

      <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth multiline maxRows={4} size="small" placeholder="Escribí tu pregunta" value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); send(); }
          }}
          inputProps={{ maxLength: 2000, 'aria-label': 'Pregunta' }}
        />
        <Button variant="contained" onClick={() => send()} disabled={loading || !input.trim()} endIcon={<SendIcon />}>Enviar</Button>
      </Stack>
    </Paper>
    </Box>
  );
}
